import { Link } from 'react-router-dom';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DEFAULT_BODY_FONT,
  DEFAULT_HEADING_FONT,
  FONT_CATEGORY_LABELS,
  FONTS,
  fontById,
  GRADE_LEVELS,
  gradeLabel,
  HEADER_COLORS,
  HEADER_STYLES,
  HEADER_STYLE_LABELS,
  canHide,
  misnumberedStudents,
  moveNav,
  navOrderToStore,
  orderNav,
  STANDARD_SUBJECT_LABELS,
  type FontCategory,
  type FontChoice,
} from '@td/shared';
import { useAuth } from '../auth/AuthContext';
import {
  useDeletePhoto,
  useStandardGrades,
  useStandards,
  useUpdateSettings,
  useUploadPhoto,
} from '../hooks/useStandards';
import { useRenumberStudents, useStudents } from '../hooks/useStudents';
import { useConfirm } from '../components/ConfirmDialog';
import { DEFAULT_THEME, THEMES, themeById } from '../theme/palette';
import { useArchiveSchoolYear, useRestoreSchoolYear, useSchoolYears } from '../hooks/useSchoolYear';
import { ARCHIVED_BY_ROLLOVER, IMAGE_RULES, KEPT_BY_ROLLOVER, schoolYearLabel } from '@td/shared';
import {
  downloadBackup,
  restoreBackup,
  useChangePassword,
  useUpdateProfile,
  type RestoreResult,
} from '../hooks/useAccount';
import { ApiRequestError } from '../lib/api';
import { useFontSamples } from '../theme/Appearance';
import {
  backgrounds as allBackgrounds,
  backgroundGroups,
  DEFAULT_BACKGROUND,
  DEFAULT_TEXTURE_STRENGTH,
  TEXTURES,
  TEXTURE_STRENGTHS,
  backgroundColor,
  backgroundImage,
  textureImage,
} from '../theme/backgrounds';
import { Doodle, DOODLE_NAMES, DEFAULT_AVATAR_MARK, isDoodle } from '../components/Doodle';
import { Icon, NAV_ITEMS, navColors, type NavItem } from '../components/Sidebar';
import { useDragOnto } from '../lib/useDragOnto';
import { TeacherMark } from '../components/TeacherMark';
import {
  clampScale,
  CUSTOM_BACKGROUND_PREFIX,
  deviceTimeZone,
  HEADING_SCALES,
  MAX_CUSTOM_BACKGROUNDS,
  searchTimeZones,
  TEXT_SCALES,
  zoneClock,
} from '@td/shared';
import {
  useCustomBackgrounds,
  useDeleteBackground,
  useUploadBackground,
} from '../hooks/useBackgrounds';
import {
  useConnectMail,
  useDiagnoseMail,
  useDisconnectMail,
  useMailAccount,
} from '../hooks/useMail';
import type { MailDiagnostics } from '@td/shared';

export function SettingsPage() {
  const { user } = useAuth();

  return (
    /*
     * Two columns, grouped by what the setting is about.
     *
     * Sixteen panels in one column was a page you scrolled and scrolled, and
     * Trevor's word for it was overwhelming. Splitting it also does the more
     * useful thing: the run-your-classroom settings and the make-it-look-right
     * settings stop being interleaved, so "where do I change the font" has an
     * obvious half of the screen to look in.
     *
     * Two fixed columns rather than a flowing grid, because these panels open
     * and shut. A grid would leave a hole beside every opened panel, and a
     * masonry layout would shuffle panels between columns as they expanded —
     * moving the thing you just clicked.
     */
    <div className="mx-auto max-w-6xl">
      <header className="page-header">
        <h1 className="text-2xl">Settings</h1>
        <p className="mt-0.5 text-sm text-stone">{user?.email}</p>
      </header>

      <div className="gap-x-5 lg:flex lg:items-start">
        <div className="min-w-0 flex-1">
          <SettingsColumn title="Your classroom" />

          <AccountSection />
          <SchoolYearSection />
          <GradeLevelSection />
          <MailSection />
          <TimeZoneSection />
          <ClassNumbering />
          <BackupSection />

          <SettingsPanel title="About the standards">
            <p className="text-sm leading-relaxed text-walnut">
              Transcribed from the Indiana Department of Education's published documents — the 2023
              Indiana Academic Standards, adopted by the State Board of Education in June 2023.
              Codes and wording match the state's own text rather than being paraphrased, so a
              standard you cite in a plan reads the way an administrator expects.
            </p>
            <p className="mt-2 text-xs text-stone">
              Standards marked <span className="text-olive">essential</span> are the ones IDOE flags
              as essential for mastery by the end of the grade.
            </p>
          </SettingsPanel>
        </div>

        <div className="min-w-0 flex-1">
          <SettingsColumn title="How it looks" />

          <ThemeSection />
          <MarkSection />
          <FontSection />
          <TextSizeSection />
          <HeaderStyleSection />
          <NavOrderSection />
          <BackgroundSection />
          <TextureSection />
        </div>
      </div>
    </div>
  );
}

/** A quiet label over each column, so the split reads as deliberate. */
function SettingsColumn({ title }: { title: string }) {
  return (
    <h2 className="mb-1.5 mt-1 px-1 text-xs uppercase tracking-wide text-stone">{title}</h2>
  );
}

/** Which grade's standards the lesson planner should offer. */
function GradeLevelSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();
  const { data: loadedGrades } = useStandardGrades();
  const [saved, setSaved] = useState(false);

  const grade = user?.settings.gradeLevel ?? '';
  const { data: standards } = useStandards(grade || null);

  const available = new Set(loadedGrades ?? []);

  const bySubject = (standards ?? []).reduce<Record<string, number>>((acc, s) => {
    acc[s.subject] = (acc[s.subject] ?? 0) + 1;
    return acc;
  }, {});

  async function onGradeChange(value: string) {
    setSaved(false);
    await updateSettings.mutateAsync({ gradeLevel: value || null });
    setSaved(true);
  }

  return (
    <>
      <SettingsPanel title="Grade level">
        <p className="mb-3 text-sm text-stone">
          Sets which academic standards appear in your lesson plans.
        </p>

        <select
          className="field max-w-xs"
          value={grade}
          onChange={(e) => void onGradeChange(e.target.value)}
          aria-label="Grade level"
        >
          <option value="">Not set</option>
          {GRADE_LEVELS.map((g) => (
            <option key={g} value={g}>
              {gradeLabel(g)}
              {available.has(g) ? '' : ' — standards not loaded yet'}
            </option>
          ))}
        </select>

        {saved && <p className="mt-2 text-xs text-olive">Saved.</p>}

        {grade && (
          <div className="mt-4 rounded-lg bg-linen/60 p-3 text-sm">
            {Object.keys(bySubject).length === 0 ? (
              <p className="text-walnut">
                No standards are loaded for {gradeLabel(grade)} yet. Everything else still works —
                the Standards field just won't offer suggestions.
              </p>
            ) : (
              <>
                <p className="mb-1 text-walnut">
                  Indiana Academic Standards loaded for {gradeLabel(grade)}:
                </p>
                <ul className="text-stone">
                  {Object.entries(bySubject).map(([subject, count]) => (
                    <li key={subject}>
                      {STANDARD_SUBJECT_LABELS[subject] ?? subject} — {count} standards
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </SettingsPanel>
    </>
  );
}

/**
 * One collapsible block of settings.
 *
 * Settings had grown to a dozen full-height sections and finding the time
 * zone meant scrolling past every font in the app. Collapsed by default, with
 * whatever was left open remembered per panel — the one being fiddled with is
 * usually the one wanted again tomorrow.
 */
function SettingsPanel({
  title,
  summary,
  children,
}: {
  title: string;
  /** A line shown while closed, so the panel says what is inside it. */
  summary?: string;
  children: ReactNode;
}) {
  const key = `td.settings.${title.toLowerCase().replace(/[^a-z]+/g, '-')}`;

  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) === 'open';
    } catch {
      return false;
    }
  });

  function toggle() {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      try {
        localStorage.setItem(key, next ? 'open' : 'shut');
      } catch {
        // Not remembered; it still holds for this session.
      }
      return next;
    });
  }

  return (
    <section className="card mb-3">
      <button
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block font-display text-base text-espresso">{title}</span>
          {summary && !open && (
            <span className="mt-0.5 block truncate text-xs text-stone">{summary}</span>
          )}
        </span>

        <span
          aria-hidden
          className="shrink-0 text-clay transition-transform"
          style={{ transform: open ? 'rotate(90deg)' : 'none' }}
        >
          ▶
        </span>
      </button>

      {open && <div className="mt-4">{children}</div>}
    </section>
  );
}

/**
 * The colour scheme for the whole app.
 *
 * Fourteen colours, and every button, border and label follows — nothing is
 * styled per theme. Each is shown as the palette itself rather than a name,
 * because "Jewel tone" means nothing until you see the emerald.
 */
function ThemeSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();

  const current = user?.settings.theme ?? DEFAULT_THEME;
  const theme = themeById(current);

  return (
    <SettingsPanel title="Theme" summary={theme.name}>
      <p className="mb-3 text-sm leading-relaxed text-walnut">
        Recolours everything — the sidebar, the buttons, the cards, the page colours below.
      </p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {THEMES.map((t) => {
          const selected = t.id === current;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => updateSettings.mutate({ theme: t.id })}
              aria-pressed={selected}
              className={[
                'rounded-lg border p-3 text-left transition-colors',
                selected
                  ? 'border-terracotta ring-1 ring-terracotta'
                  : 'border-sand hover:border-clay',
              ].join(' ')}
              style={{ backgroundColor: t.palette.cream }}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm" style={{ color: t.palette.espresso }}>
                  {t.name}
                </span>
                {selected && (
                  <span className="text-[10px]" style={{ color: t.palette.terracotta }}>
                    in use
                  </span>
                )}
              </span>

              <span className="mt-1 block text-xs" style={{ color: t.palette.stone }}>
                {t.blurb}
              </span>

              {/* The palette itself: the eight colours anyone will notice. */}
              <span className="mt-2 flex gap-1">
                {(
                  ['terracotta', 'rust', 'ochre', 'olive', 'sage', 'rose', 'mauve', 'clay'] as const
                ).map((role) => (
                  <span
                    key={role}
                    className="h-5 flex-1 rounded"
                    style={{ backgroundColor: t.palette[role] }}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </SettingsPanel>
  );
}

/**
 * Connecting a Gmail account.
 *
 * An app password rather than "Sign in with Google": reading Gmail is a
 * restricted OAuth scope, and until Google verifies an app it can only stay
 * in testing mode, where the connection expires every seven days. Asking a
 * teacher to reconnect her inbox every week is not a feature. An app password
 * is scoped to this one mailbox and keeps working until she revokes it.
 */
function MailSection() {
  const { data: state, isLoading } = useMailAccount();
  const connect = useConnectMail();
  const disconnect = useDisconnectMail();
  const confirm = useConfirm();

  const [email, setEmail] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const diagnose = useDiagnoseMail();
  const [report, setReport] = useState<MailDiagnostics | null>(null);

  async function onConnect() {
    setError(null);
    setReport(null);
    try {
      await connect.mutateAsync({
        email: email.trim(),
        appPassword,
        displayName: displayName.trim(),
      });
      setAppPassword('');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That did not connect');

      // A failed connection is exactly when the transcript is wanted, so it
      // runs itself rather than waiting to be asked a second time.
      void runTest();
    }
  }

  async function runTest() {
    setReport(null);
    try {
      const result = await diagnose.mutateAsync({
        email: email.trim() || undefined,
        appPassword: appPassword || undefined,
      });
      setReport(result);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'The test could not run');
    }
  }

  if (isLoading) return null;

  const account = state?.account ?? null;

  return (
    <SettingsPanel title="Email">
      <p className="mb-3 text-sm leading-relaxed text-walnut">
        Puts your Gmail on the dashboard — read it, reply, archive, all without leaving.
      </p>

      {!state?.configured && (
        <p className="mb-3 rounded-lg border border-ochre/50 bg-ochre/10 p-3 text-sm text-walnut">
          The server needs a MAIL_SECRET environment variable before a mailbox can be saved. It
          encrypts the stored password.
        </p>
      )}

      {account ? (
        <>
          <div className="mb-3 rounded-lg border border-sand bg-linen/50 p-3 text-sm">
            <p className="text-espresso">{account.email}</p>
            <p className="mt-0.5 text-xs text-stone">
              Connected {new Date(account.connectedAt).toLocaleDateString()}
              {account.lastSyncAt
                ? ` · last checked ${new Date(account.lastSyncAt).toLocaleString()}`
                : ''}
            </p>
            {account.lastError && <p className="mt-1 text-xs text-rust">{account.lastError}</p>}
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            <button
              className="btn-secondary"
              onClick={() => void runTest()}
              disabled={diagnose.isPending}
            >
              {diagnose.isPending ? 'Testing…' : 'Test the connection'}
            </button>
          </div>

          <Report report={report} />

          <button
            className="btn-danger"
            onClick={async () => {
              const ok = await confirm({
                title: 'Disconnect this mailbox?',
                body: 'The saved password is deleted. Nothing in Gmail itself is touched.',
                confirmLabel: 'Disconnect',
                danger: true,
              });
              if (ok) disconnect.mutate();
            }}
          >
            Disconnect
          </button>
        </>
      ) : (
        <>
          {/*
            Written out in full rather than linked, because the app password
            page is three levels deep in Google's account settings and is not
            findable by guessing.
          */}
          <ol className="mb-4 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-walnut">
            <li>
              Turn on 2-step verification for the account at{' '}
              <span className="text-espresso">myaccount.google.com/security</span> — Google will not
              offer app passwords without it.
            </li>
            <li>
              Go to <span className="text-espresso">myaccount.google.com/apppasswords</span>, name
              it something like &ldquo;Teacher Dashboard&rdquo;, and press Create.
            </li>
            <li>Copy the sixteen characters it shows you and paste them below. Spaces are fine.</li>
          </ol>

          <div className="space-y-3">
            <div>
              <label className="label" htmlFor="mail-email">
                Gmail address
              </label>
              <input
                id="mail-email"
                className="field"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gmail.com"
              />
            </div>

            <div>
              <label className="label" htmlFor="mail-password">
                App password
              </label>
              <input
                id="mail-password"
                className="field"
                type="password"
                autoComplete="off"
                value={appPassword}
                onChange={(e) => setAppPassword(e.target.value)}
                placeholder="sixteen characters"
              />
              <p className="mt-1 text-xs text-stone">
                Not your Google password. It is stored encrypted and only ever sent to Gmail.
              </p>
            </div>

            <div>
              <label className="label" htmlFor="mail-name">
                Name on messages you send
              </label>
              <input
                id="mail-name"
                className="field"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Megan Curry"
              />
            </div>
          </div>

          {error && <p className="mt-2 text-sm text-rust">{error}</p>}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              className="btn-primary"
              onClick={() => void onConnect()}
              disabled={!email.trim() || !appPassword.trim() || connect.isPending}
            >
              {connect.isPending ? 'Checking with Gmail…' : 'Connect'}
            </button>

            <button
              className="btn-secondary"
              onClick={() => void runTest()}
              disabled={!email.trim() || !appPassword.trim() || diagnose.isPending}
            >
              {diagnose.isPending ? 'Testing…' : 'Test without saving'}
            </button>
          </div>

          <Report report={report} />

          <p className="mt-2 text-xs text-stone">
            If this is a school address, the district may have app passwords switched off. A
            personal Gmail always works.
          </p>
        </>
      )}
    </SettingsPanel>
  );
}

/**
 * The connection transcript.
 *
 * Every stage, in order, with whatever the server actually said — not a
 * paraphrase. The summary at the top is the sentence worth acting on; the
 * steps below it exist so that when the summary is wrong, the real answer is
 * still on the screen. Copy puts the whole thing on the clipboard, because
 * the person who can fix a Workspace policy is usually not the person sitting
 * in front of it.
 */
function Report({ report }: { report: MailDiagnostics | null }) {
  const [copied, setCopied] = useState(false);

  if (!report) return null;

  const asText = [
    `Mailbox test — ${report.email}`,
    `Password came from: ${report.passwordSource === 'stored' ? 'the saved account' : 'the form'}`,
    `Result: ${report.ok ? 'passed' : 'failed'}`,
    `Summary: ${report.summary}`,
    '',
    ...report.steps.map((s) =>
      [
        `${s.ok ? 'PASS' : 'FAIL'}  ${s.name} (${s.ms}ms)`,
        `      ${s.detail}`,
        s.error ? `      error: ${s.error}` : null,
        s.serverSaid ? `      server: ${s.serverSaid}` : null,
      ]
        .filter(Boolean)
        .join('\n'),
    ),
  ].join('\n');

  return (
    <div className="mt-4 rounded-lg border border-sand bg-linen/40 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className={report.ok ? 'text-olive' : 'text-rust'}>
          {report.ok ? 'Everything passed' : 'Something failed'}
        </h3>

        <button
          className="btn-soft"
          onClick={() => {
            void navigator.clipboard.writeText(asText).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy the whole report'}
        </button>
      </div>

      <p className="mb-3 text-sm leading-relaxed text-espresso">{report.summary}</p>

      <ol className="space-y-2">
        {report.steps.map((step) => (
          <li
            key={step.name}
            className="rounded-lg border bg-white p-2.5"
            style={{ borderColor: step.ok ? '#DCCDBB' : '#9A604566' }}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm text-espresso">
                <span className={step.ok ? 'text-olive' : 'text-rust'}>{step.ok ? '✓' : '✕'}</span>{' '}
                {step.name}
              </span>
              <span className="shrink-0 text-[11px] text-stone">{step.ms}ms</span>
            </div>

            <p className="mt-0.5 text-xs leading-relaxed text-walnut">{step.detail}</p>

            {step.error && (
              <p className="mt-1 break-words font-mono text-[11px] leading-relaxed text-rust">
                {step.error}
              </p>
            )}

            {step.serverSaid && (
              <p className="mt-1 break-words font-mono text-[11px] leading-relaxed text-walnut">
                {step.serverSaid}
              </p>
            )}
          </li>
        ))}
      </ol>

      <p className="mt-2 text-xs text-stone">
        The same transcript is in the server log, under [mail] diagnostics.
      </p>
    </div>
  );
}

/**
 * The little drawn mark that stands in for a photograph.
 *
 * Line art rather than emoji: emoji are a different picture on every machine,
 * bring their own colours, and half of them look like a toy next to a page of
 * assessment data.
 */
function MarkSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();
  const uploadPhoto = useUploadPhoto();
  const deletePhoto = useDeletePhoto();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const mark = user?.settings.avatarMark;
  const current =
    mark === 'none' || mark === 'photo' ? mark : isDoodle(mark) ? mark : DEFAULT_AVATAR_MARK;

  const chip = (selected: boolean) =>
    [
      'flex h-10 items-center justify-center rounded-lg border px-3 text-xs transition-colors',
      selected
        ? 'border-terracotta bg-terracotta text-cream'
        : 'border-sand bg-white text-walnut hover:border-clay hover:bg-linen',
    ].join(' ');

  async function onFile(file: File) {
    setError(null);
    try {
      await uploadPhoto.mutateAsync(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That photo could not be used');
    }
  }

  return (
    <SettingsPanel title="Your mark">
      <p className="mb-3 text-sm leading-relaxed text-walnut">
        Sits beside your name in the sidebar. A drawing, a photograph of you, or nothing at all.
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => updateSettings.mutate({ avatarMark: 'none' })}
          aria-pressed={current === 'none'}
          className={chip(current === 'none')}
        >
          No mark
        </button>

        <input
          ref={fileInput}
          type="file"
          accept={IMAGE_RULES.acceptedMimeTypes.join(',')}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
            e.target.value = '';
          }}
        />

        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploadPhoto.isPending}
          className={chip(false)}
        >
          {uploadPhoto.isPending
            ? 'Uploading…'
            : user?.hasPhoto
              ? 'Change the photo'
              : 'Upload a photo'}
        </button>

        {user?.hasPhoto && (
          <button type="button" onClick={() => deletePhoto.mutate()} className="btn-soft-danger">
            Remove the photo
          </button>
        )}
      </div>

      {error && <p className="mb-2 text-xs text-rust">{error}</p>}

      {/*
        Shown at the size it is actually used, not as a thumbnail. A photo of
        a person judged at 20 pixels is a photo of nobody.
      */}
      {user?.hasPhoto && (
        <button
          type="button"
          onClick={() => updateSettings.mutate({ avatarMark: 'photo' })}
          aria-pressed={current === 'photo'}
          className={[
            'mb-3 flex w-full items-center gap-3 rounded-card border p-3 text-left transition-colors',
            current === 'photo'
              ? 'border-terracotta bg-terracotta/10'
              : 'border-sand bg-white hover:border-clay hover:bg-linen',
          ].join(' ')}
        >
          <TeacherMark
            user={{ ...user, settings: { ...user.settings, avatarMark: 'photo' } }}
            size={72}
            className="shadow-soft"
          />
          <span className="min-w-0">
            <span className="block text-sm text-espresso">Use my photo</span>
            <span className="block text-xs text-stone">
              {current === 'photo'
                ? 'This is what the sidebar shows.'
                : 'Shown above your name in the sidebar.'}
            </span>
          </span>
        </button>
      )}

      <p className="mb-2 text-xs text-stone">Or one of these:</p>

      <div className="flex flex-wrap gap-1.5">
        {DOODLE_NAMES.map((name) => {
          const selected = name === current;
          return (
            <button
              key={name}
              type="button"
              onClick={() => updateSettings.mutate({ avatarMark: name })}
              aria-pressed={selected}
              aria-label={name.replace(/-/g, ' ')}
              title={name.replace(/-/g, ' ')}
              className={[
                'flex h-10 w-10 items-center justify-center rounded-lg border transition-colors',
                selected
                  ? 'border-terracotta bg-terracotta text-cream'
                  : 'border-sand bg-white text-walnut hover:border-clay hover:bg-linen',
              ].join(' ')}
            >
              <Doodle name={name} size={20} />
            </button>
          );
        })}
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-stone">
        A photo is resized and re-encoded here before it is stored, which strips the location data
        phone cameras attach. It is served only to you, over your own session.
      </p>
    </SettingsPanel>
  );
}

/**
 * How big the type is, independently of which type it is.
 *
 * Everything in the app is sized in rem, so the first control resizes the
 * whole interface — boxes, padding and buttons along with the words. Scaling
 * only the text would leave larger letters inside the same small containers,
 * which is worse to read than where it started.
 */
function TextSizeSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();

  const text = clampScale(user?.settings.textScale);
  const heading = clampScale(user?.settings.headingScale);

  return (
    <SettingsPanel title="Text size">
      <p className="mb-4 text-sm leading-relaxed text-walnut">
        Applies everywhere, including printouts.
      </p>

      <div className="mb-4">
        <p className="mb-1.5 text-xs uppercase tracking-wide text-stone">Everything</p>
        <div className="flex flex-wrap gap-2">
          {TEXT_SCALES.map((step) => (
            <ScaleButton
              key={step.value}
              label={step.label}
              scale={step.value}
              selected={step.value === text}
              onPick={() => updateSettings.mutate({ textScale: step.value })}
            />
          ))}
        </div>
      </div>

      <div className="mb-4">
        <p className="mb-1.5 text-xs uppercase tracking-wide text-stone">
          Headings, on top of that
        </p>
        <div className="flex flex-wrap gap-2">
          {HEADING_SCALES.map((step) => (
            <ScaleButton
              key={step.value}
              label={step.label}
              scale={step.value}
              selected={step.value === heading}
              onPick={() => updateSettings.mutate({ headingScale: step.value })}
            />
          ))}
        </div>
      </div>

      {/* No inline sizing — both scales are already live on this very page. */}
      <div className="rounded-lg border border-sand bg-linen/50 p-4">
        <h3>Today&rsquo;s lesson plan</h3>
        <p className="mt-1 text-sm text-walnut">
          Reading at 9:45. Twenty-four in class, none absent.
        </p>
      </div>

      {(text !== 1 || heading !== 1) && (
        <button
          type="button"
          className="btn-secondary mt-4"
          onClick={() => updateSettings.mutate({ textScale: 1, headingScale: 1 })}
        >
          Back to the standard sizes
        </button>
      )}
    </SettingsPanel>
  );
}

/**
 * The bar at the top of every page, either way round.
 *
 * The colour is the section's own, so the calendar's header stays mauve and
 * the roster's stays rust — this changes how much of it there is, not which
 * one it is. Filled uses the shade already derived to carry light text, so it
 * is readable in all ten themes by construction rather than by ten separate
 * decisions.
 */
function HeaderStyleSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();

  const current = user?.settings.headerStyle ?? 'plain';
  const colour = user?.settings.headerColor ?? null;

  return (
    <SettingsPanel title="Page headers">
      <p className="mb-4 text-sm leading-relaxed text-walnut">
        The bar across the top of each page — how much colour it carries, and which colour.
      </p>

      {/* ------------------------------------------------------ the colour */}
      <p className="mb-1.5 text-xs uppercase tracking-wide text-stone">Colour</p>

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => updateSettings.mutate({ headerColor: null })}
          aria-pressed={colour === null}
          className={colour === null ? 'btn-primary' : 'btn-soft'}
        >
          Match the page
        </button>

        {HEADER_COLORS.map((name) => (
          <button
            key={name}
            type="button"
            title={name}
            aria-label={name}
            aria-pressed={colour === name}
            onClick={() => updateSettings.mutate({ headerColor: name })}
            className={[
              'h-8 w-8 rounded-full border-2 transition-transform',
              colour === name ? 'scale-110 border-espresso' : 'border-sand hover:scale-105',
            ].join(' ')}
            // The theme's own colour, read from the variable rather than
            // written down — so these swatches change with the theme, which
            // is the whole point of picking a name instead of a colour.
            style={{ backgroundColor: `rgb(var(--c-${name}))` }}
          />
        ))}
      </div>

      <p className="mb-4 text-xs leading-snug text-stone">
        <strong>Match the page</strong> keeps what each section already has — the calendar mauve,
        the roster rust. Pick one instead and every header wears it. Either way it is that
        theme&rsquo;s version of the colour, so it follows when you change themes.
      </p>

      <p className="mb-1.5 text-xs uppercase tracking-wide text-stone">Style</p>

      <div className="space-y-2">
        {HEADER_STYLES.map((style) => (
          <button
            key={style}
            type="button"
            onClick={() => updateSettings.mutate({ headerStyle: style })}
            aria-pressed={current === style}
            className={[
              'block w-full rounded-card border-2 p-3 text-left transition-colors',
              current === style ? 'border-terracotta' : 'border-sand hover:border-clay',
            ].join(' ')}
          >
            {/*
              A real one, not a description of one. Both use the accent
              variables the page headers use, so what is shown here is what
              lands on every page.
            */}
            <span
              className="block rounded-lg px-3 py-2"
              style={
                style === 'filled'
                  ? { backgroundColor: 'var(--header-fill)', color: 'rgb(var(--c-cream))' }
                  : {
                      backgroundColor: 'var(--header-wash)',
                      borderLeft: '4px solid var(--header-base)',
                    }
              }
            >
              <span className="block font-display text-lg leading-tight">Good afternoon, Megan</span>
              <span
                className="block text-xs"
                style={{
                  color: style === 'filled' ? 'rgb(var(--c-cream) / 0.82)' : undefined,
                }}
              >
                Friday, August 7
              </span>
            </span>

            <span className="mt-2 block text-xs text-stone">{HEADER_STYLE_LABELS[style]}</span>
          </button>
        ))}
      </div>
    </SettingsPanel>
  );
}

/**
 * The sidebar, in whatever order she wants it.
 *
 * The shipped order is somebody's guess at a school day, and it will be wrong
 * for somebody. A teacher who opens Lesson plans twice a day and Data twice a
 * year should not have Data above Lesson plans.
 *
 * Two ways to move a row, on purpose. Dragging is what everybody reaches for
 * first, and it is also the one that goes wrong — a slip, a trackpad, a row
 * near the top of a scrolled list. So every row also carries a pair of arrows,
 * which are exact, keyboard-reachable, and impossible to drop in the wrong
 * place.
 *
 * These are the real rows: the real colours, the real icons, in the real
 * order, changing in the sidebar beside them as they move. A preview drawn
 * from a description of the sidebar is a second thing to keep true.
 */
function NavOrderSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();

  const colors = navColors();

  /*
   * Every page is listed here, switched off ones included — this is the panel
   * where a hidden page is put back, so it is the one place that must not
   * hide anything.
   */
  const items = orderNav(NAV_ITEMS, user?.settings.navOrder);
  const arranged = user?.settings.navOrder != null;

  const hidden = useMemo(
    () => new Set<string>(user?.settings.navHidden ?? []),
    [user?.settings.navHidden],
  );

  /** Save an order, or clear it when it is the shipped one again. */
  function save(next: NavItem[]) {
    updateSettings.mutate({ navOrder: navOrderToStore(next, NAV_ITEMS) });
  }

  /** Switch one page on or off. Empty is stored as null, not as an empty list. */
  function toggle(route: string) {
    if (!canHide(route)) return;

    const next = new Set(hidden);
    if (!next.delete(route)) next.add(route);

    updateSettings.mutate({ navHidden: next.size > 0 ? [...next] : null });
  }

  function nudge(from: number, to: number) {
    if (to < 0 || to >= items.length) return;
    save(moveNav(items, from, to));
  }

  /*
   * Dropping a row on another row puts it where that one was, and everything
   * between shuffles up or down by one. Which is what the gesture looks like
   * it should do: the row you let go over is the place you chose.
   */
  const drag = useDragOnto<string>((route, onto) => {
    const from = items.findIndex((item) => item.to === route);
    const to = items.findIndex((item) => item.to === onto);
    if (from !== -1 && to !== -1) save(moveNav(items, from, to));
  });

  return (
    <SettingsPanel
      title="Sidebar"
      summary={
        [arranged ? 'your own order' : null, hidden.size > 0 ? `${hidden.size} hidden` : null]
          .filter(Boolean)
          .join(', ') || undefined
      }
    >
      <p className="mb-4 text-sm leading-relaxed text-walnut">
        Drag a page up or down, or use the arrows. Switch off the ones you never open. The sidebar
        changes as you go.
      </p>

      <ul className="space-y-1">
        {items.map((item, i) => {
          const color = colors[item.to] ?? colors['/']!;
          const dragging = drag.item === item.to;
          const over = drag.over === item.to && !dragging;
          const off = hidden.has(item.to);

          return (
            <li key={item.to}>
              <div
                ref={(el) => drag.registerTarget(item.to, el)}
                onPointerDown={(e) => drag.begin(item.to, e)}
                className={[
                  'flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors',
                  dragging ? 'opacity-40' : '',
                  over ? 'ring-2 ring-terracotta' : '',
                ].join(' ')}
                style={{
                  // A switched-off row stays in place and goes pale, rather
                  // than vanishing from the list it is switched off from.
                  backgroundColor: off ? 'transparent' : color.wash,
                  color: off ? undefined : color.label,
                  cursor: 'grab',
                }}
              >
                {/* Six dots, the universal "this one moves". */}
                <span aria-hidden="true" className="select-none text-xs tracking-tight opacity-60">
                  ⠿
                </span>

                <span className={off ? 'text-stone/60' : undefined}>
                  <Icon name={item.icon} />
                </span>
                <span className={['flex-1 text-sm', off ? 'text-stone line-through' : ''].join(' ')}>
                  {item.label}
                </span>

                <span data-no-drag>
                  <ShowHideButton
                    label={item.label}
                    hidden={off}
                    fixed={!canHide(item.to)}
                    onClick={() => toggle(item.to)}
                  />
                </span>

                {/*
                  Marked no-drag so a press on an arrow stays a press. The row
                  around them claims the pointer once it has travelled, and a
                  button inside a draggable row is exactly how the ✕ on the
                  jobs board came to look broken.
                */}
                <span className="flex items-center gap-1" data-no-drag>
                  <NudgeButton
                    direction="up"
                    label={`Move ${item.label} up`}
                    disabled={i === 0}
                    onClick={() => nudge(i, i - 1)}
                  />
                  <NudgeButton
                    direction="down"
                    label={`Move ${item.label} down`}
                    disabled={i === items.length - 1}
                    onClick={() => nudge(i, i + 1)}
                  />
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 flex flex-wrap gap-2">
        {arranged && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => updateSettings.mutate({ navOrder: null })}
          >
            Back to the order it came in
          </button>
        )}

        {hidden.size > 0 && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => updateSettings.mutate({ navHidden: null })}
          >
            Show all {hidden.size} again
          </button>
        )}
      </div>

      <p className="mt-3 text-xs leading-snug text-stone">
        Switching a page off only takes it off the sidebar. Nothing on it is deleted, and a
        bookmark to it still works. Pages added in a later update appear where they were meant to
        sit, rather than at the bottom — setting this up once does not mean missing what comes
        next.
      </p>
    </SettingsPanel>
  );
}

/**
 * On or off, as an eye.
 *
 * Settings itself gets a struck-through eye that does nothing but explain
 * itself: hiding it would hide the panel holding this button, and the only way
 * back would be typing a URL. Better to show why it cannot be done than to
 * leave a gap where a control should be.
 */
function ShowHideButton({
  label,
  hidden,
  fixed,
  onClick,
}: {
  label: string;
  hidden: boolean;
  fixed: boolean;
  onClick: () => void;
}) {
  const title = fixed
    ? 'Settings always stays — it is where this switch lives'
    : hidden
      ? `Show ${label} in the sidebar`
      : `Hide ${label} from the sidebar`;

  return (
    <button
      type="button"
      aria-label={title}
      aria-pressed={!hidden}
      title={title}
      disabled={fixed}
      onClick={onClick}
      className="rounded-md border border-transparent bg-white/60 p-1 transition-colors hover:border-clay hover:bg-white disabled:cursor-default disabled:opacity-30 disabled:hover:border-transparent disabled:hover:bg-white/60"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        width={14}
        height={14}
        aria-hidden="true"
      >
        <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
        <circle cx="12" cy="12" r="2.6" />
        {(hidden || fixed) && <path d="M4 20 20 4" />}
      </svg>
    </button>
  );
}

/** One step up or down. Small, but a button — nothing here is a bare arrow. */
function NudgeButton({
  direction,
  label,
  disabled,
  onClick,
}: {
  direction: 'up' | 'down';
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-md border border-transparent bg-white/60 p-1 transition-colors hover:border-clay hover:bg-white disabled:cursor-default disabled:opacity-25 disabled:hover:border-transparent disabled:hover:bg-white/60"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        width={14}
        height={14}
        aria-hidden="true"
      >
        <path d={direction === 'up' ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
      </svg>
    </button>
  );
}

function ScaleButton({
  label,
  scale,
  selected,
  onPick,
}: {
  label: string;
  scale: number;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      className={[
        'rounded-lg border px-3 py-2 transition-colors',
        selected
          ? 'border-terracotta bg-terracotta text-cream'
          : 'border-sand bg-white text-espresso hover:border-clay hover:bg-linen',
      ].join(' ')}
    >
      {/* Shown at the size it sets, so the label demonstrates itself. */}
      <span style={{ fontSize: `calc(0.875rem * ${scale})` }}>{label}</span>
    </button>
  );
}

/**
 * Backgrounds the teacher uploaded herself.
 *
 * They are kept until she deletes them, so switching to Pumpkins in October
 * and back to her classroom photo in November costs nothing. Removing the one
 * in use clears the setting server-side rather than leaving every page asking
 * for an image that no longer exists.
 */
function YourBackgrounds({ current, onPick }: { current: string; onPick: (id: string) => void }) {
  const { data: backgrounds = [] } = useCustomBackgrounds();
  const upload = useUploadBackground();
  const remove = useDeleteBackground();
  const confirm = useConfirm();

  const [error, setError] = useState<string | null>(null);
  const full = backgrounds.length >= MAX_CUSTOM_BACKGROUNDS;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Clear the input either way, so picking the same file twice still fires.
    e.target.value = '';
    if (!file) return;

    setError(null);
    try {
      const created = await upload.mutateAsync({ file });
      onPick(`${CUSTOM_BACKGROUND_PREFIX}${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That image couldn't be uploaded");
    }
  }

  return (
    <div className="mb-5">
      <p className="mb-1.5 text-xs uppercase tracking-wide text-stone">Yours</p>

      {backgrounds.length > 0 && (
        <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {backgrounds.map((bg) => {
            const id = `${CUSTOM_BACKGROUND_PREFIX}${bg.id}`;
            const selected = id === current;

            return (
              <div
                key={bg.id}
                className={[
                  'overflow-hidden rounded-lg border transition-colors',
                  selected ? 'border-terracotta ring-1 ring-terracotta' : 'border-sand',
                ].join(' ')}
              >
                <button
                  type="button"
                  onClick={() => onPick(id)}
                  aria-pressed={selected}
                  className="block w-full"
                >
                  <img
                    src={`/api/backgrounds/${bg.id}/image`}
                    alt=""
                    loading="lazy"
                    className="h-16 w-full bg-linen object-cover"
                  />
                  <span
                    className={[
                      'block truncate px-2 py-1.5 text-left text-xs',
                      selected ? 'bg-terracotta text-cream' : 'bg-white text-walnut',
                    ].join(' ')}
                  >
                    {bg.name}
                  </span>
                </button>

                <div className="border-t border-sand/60 bg-white px-2 py-1">
                  <button
                    type="button"
                    className="btn-soft-danger w-full"
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Remove "${bg.name}"?`,
                        body: 'The image is deleted. You would need to upload it again.',
                        confirmLabel: 'Remove it',
                        danger: true,
                      });
                      if (ok) remove.mutate(bg.id);
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/*
        A label styled as a button, because a bare file input cannot be made
        to look like anything else and every other control here is a button.
      */}
      <label
        className={[
          'btn-secondary cursor-pointer',
          full || upload.isPending ? 'pointer-events-none opacity-60' : '',
        ].join(' ')}
      >
        {upload.isPending ? 'Uploading…' : 'Upload your own'}
        <input type="file" accept="image/*" className="sr-only" onChange={(e) => void onFile(e)} />
      </label>

      <p className="mt-1.5 text-xs text-stone">
        {full
          ? `That is all ${MAX_CUSTOM_BACKGROUNDS}. Remove one to add another.`
          : 'JPEG, PNG or WebP, up to 8 MB. It is resized and re-saved on the way in.'}
      </p>

      {error && <p className="mt-1.5 text-xs text-rust">{error}</p>}
    </div>
  );
}

/**
 * A texture laid over whatever background is chosen.
 *
 * Its own setting rather than a set of pre-textured backgrounds. Baking the
 * two together meant fourteen colours times a dozen weaves, which is a picker
 * nobody can read — and they are genuinely separate decisions. Pick the
 * paper, then pick the grain.
 */
function TextureSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();

  const current = user?.settings.backgroundTexture ?? 'none';
  const strength = user?.settings.textureStrength ?? DEFAULT_TEXTURE_STRENGTH;
  const background = user?.settings.background ?? DEFAULT_BACKGROUND;
  const themePalette = themeById(user?.settings.theme).palette;

  return (
    <SettingsPanel title="Texture">
      <p className="mb-3 text-sm leading-relaxed text-walnut">
        Woven over the background, whichever one you picked. Every sample below shows it on your
        current colour.
      </p>

      <div className="mb-4">
        <p className="mb-1.5 text-xs uppercase tracking-wide text-stone">How heavy</p>
        <div className="flex flex-wrap gap-2">
          {TEXTURE_STRENGTHS.map((step) => (
            <button
              key={step.value}
              type="button"
              onClick={() => updateSettings.mutate({ textureStrength: step.value })}
              aria-pressed={Math.abs(strength - step.value) < 0.001}
              className={Math.abs(strength - step.value) < 0.001 ? 'btn-soft-accent' : 'btn-soft'}
            >
              {step.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {TEXTURES.map((texture) => {
          const selected = texture.id === current;
          return (
            <button
              key={texture.id}
              type="button"
              onClick={() => updateSettings.mutate({ backgroundTexture: texture.id })}
              aria-pressed={selected}
              className={[
                'overflow-hidden rounded-lg border transition-colors',
                selected
                  ? 'border-terracotta ring-1 ring-terracotta'
                  : 'border-sand hover:border-clay',
              ].join(' ')}
            >
              {/*
                Shown over the chosen background at a heavier hand than the
                page uses. At the real strength a swatch this small is an
                empty square, and a picker of empty squares helps nobody.
              */}
              <span
                className="block h-14 w-full"
                style={{
                  backgroundColor: backgroundColor(background, themePalette),
                  backgroundImage: textureImage(texture.id, Math.max(strength * 2.5, 0.16)),
                }}
              />
              <span
                className={[
                  'block truncate px-1.5 py-1 text-[11px]',
                  selected ? 'bg-terracotta text-cream' : 'bg-white text-walnut',
                ].join(' ')}
              >
                {texture.name}
              </span>
            </button>
          );
        })}
      </div>
    </SettingsPanel>
  );
}

/**
 * Wallpaper for the whole app.
 *
 * Deliberately faint. This sits behind lesson plans and reading scores for
 * seven hours a day, and anything with real contrast in it stops being
 * charming somewhere around the second week of term.
 */
function BackgroundSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();

  const current = user?.settings.background ?? DEFAULT_BACKGROUND;

  /*
   * Read from the chosen theme, not from module state.
   *
   * This renders in the same pass that the theme changes in, before the
   * effect that publishes the new palette has run — so reading ambiently
   * would show the previous theme's colours until something else forced a
   * repaint.
   */
  const themePalette = themeById(user?.settings.theme).palette;

  return (
    <SettingsPanel title="Background">
      <p className="mb-4 text-sm leading-relaxed text-walnut">
        A soft pattern behind everything. Swap it with the season, or leave it plain — it never
        prints, so your paperwork stays clean either way. The page colours are your theme&rsquo;s
        own, so they change when the theme does.
      </p>

      <YourBackgrounds
        current={current}
        onPick={(id) => updateSettings.mutate({ background: id })}
      />

      {backgroundGroups(themePalette).map((group) => (
        <div key={group} className="mb-4">
          <p className="mb-1.5 text-xs uppercase tracking-wide text-stone">{group}</p>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {allBackgrounds(themePalette)
              .filter((b) => b.group === group)
              .map((bg) => {
                const selected = bg.id === current;
                return (
                  <button
                    key={bg.id}
                    type="button"
                    onClick={() => updateSettings.mutate({ background: bg.id })}
                    aria-pressed={selected}
                    className={[
                      'overflow-hidden rounded-lg border text-left transition-colors',
                      selected
                        ? 'border-terracotta ring-1 ring-terracotta'
                        : 'border-sand hover:border-clay',
                    ].join(' ')}
                  >
                    {/*
                    The swatch is drawn at full strength rather than the on-page
                    opacity — at 50% most of these are invisible in a box this
                    small, and a picker of blank rectangles helps nobody.
                  */}
                    <span
                      className="block h-16 w-full"
                      style={{
                        backgroundColor: backgroundColor(bg.id),
                        backgroundImage: backgroundImage(bg.id, 1),
                      }}
                    />
                    <span
                      className={[
                        'block px-2 py-1.5 text-xs',
                        selected ? 'bg-terracotta text-cream' : 'bg-white text-walnut',
                      ].join(' ')}
                    >
                      {bg.name}
                    </span>
                  </button>
                );
              })}
          </div>
        </div>
      ))}
    </SettingsPanel>
  );
}

/**
 * The time zone every date and clock in the app is read in.
 *
 * Worth doing properly: the server runs in UTC, the browser reports whatever
 * the laptop was last set to, and Indiana alone spans two zones across eight
 * IANA entries. Searching matches town names, so "Elkhart" is enough.
 */
function TimeZoneSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();
  const [query, setQuery] = useState('');

  const device = deviceTimeZone();
  const current = user?.settings.timeZone ?? device;
  const following = user?.settings.timeZone === null || user?.settings.timeZone === undefined;

  const matches = searchTimeZones(query).slice(0, 40);
  const regions = [...new Set(matches.map((z) => z.region))];

  return (
    <SettingsPanel title="Time zone">
      <p className="mb-3 text-sm leading-relaxed text-walnut">
        Sets which day the dashboard calls "today" and which times events show at.
      </p>

      <div className="mb-3 rounded-lg border border-sand bg-linen/50 p-3 text-sm">
        <p className="text-espresso">{zoneClock(current)}</p>
        <p className="mt-0.5 text-xs text-stone">
          {current.replace(/_/g, ' ')}
          {following ? ' — following this device' : ''}
        </p>
      </div>

      <input
        className="field mb-2"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search a town, state or zone — Elkhart, Indiana, Chicago…"
        aria-label="Search time zones"
      />

      {!following && (
        <button
          type="button"
          className="btn-soft mb-3"
          onClick={() => updateSettings.mutate({ timeZone: null })}
        >
          Follow this device instead
        </button>
      )}

      <div className="max-h-80 overflow-y-auto rounded-lg border border-sand">
        {matches.length === 0 && (
          <p className="p-3 text-sm text-stone">
            Nothing matched "{query}". Try the nearest larger town, or the state.
          </p>
        )}

        {regions.map((region) => (
          <div key={region}>
            <p className="sticky top-0 bg-linen px-3 py-1 text-xs uppercase tracking-wide text-stone">
              {region}
            </p>

            {matches
              .filter((z) => z.region === region)
              .map((z) => {
                const selected = !following && z.zone === current;
                return (
                  <button
                    key={`${region}-${z.zone}`}
                    type="button"
                    onClick={() => updateSettings.mutate({ timeZone: z.zone })}
                    aria-pressed={selected}
                    className={[
                      'flex w-full items-center justify-between gap-3 border-t border-sand/60 px-3 py-2 text-left text-sm transition-colors',
                      selected
                        ? 'bg-terracotta text-cream'
                        : 'bg-white text-espresso hover:bg-linen',
                    ].join(' ')}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{z.label}</span>
                      <span
                        className={[
                          'block truncate text-xs',
                          selected ? 'text-cream/80' : 'text-stone',
                        ].join(' ')}
                      >
                        {z.cities.slice(0, 4).join(' · ')}
                      </span>
                    </span>
                    <span
                      className={[
                        'shrink-0 text-xs',
                        selected ? 'text-cream/90' : 'text-stone',
                      ].join(' ')}
                    >
                      {zoneClock(z.zone)}
                    </span>
                  </button>
                );
              })}
          </div>
        ))}
      </div>
    </SettingsPanel>
  );
}

/**
 * The whole app's typeface, chosen from the list rather than typed.
 *
 * Two choices, not one: headings carry the personality and the body text has
 * to survive a full page of lesson plans, so a teacher can have Luckiest Guy
 * across the top without reading a week's plan in it. Every sample is drawn in
 * the font it names — a list of font names set in one font tells you nothing.
 */
function FontSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();
  const [open, setOpen] = useState(false);

  const body = user?.settings.bodyFont ?? DEFAULT_BODY_FONT;
  const heading = user?.settings.headingFont ?? DEFAULT_HEADING_FONT;
  const isDefault = body === DEFAULT_BODY_FONT && heading === DEFAULT_HEADING_FONT;

  // Only fetched while the pickers are actually open — nearly fifty families
  // is a lot to pull down for a page you opened to change your password.
  useFontSamples(open ? FONTS.map((f) => f.id) : []);

  const categories = [...new Set(FONTS.map((f) => f.category))] as FontCategory[];

  return (
    <SettingsPanel title="Fonts">
      <p className="mb-3 text-sm leading-relaxed text-walnut">
        Changes the lettering everywhere — every page, and everything you print. Pick something you
        will enjoy looking at all year.
      </p>

      {/* What the choice actually looks like, before committing to it. */}
      <div className="mb-4 rounded-lg border border-sand bg-linen/50 p-4">
        <p className="text-xl" style={{ fontFamily: fontById(heading)?.stack }}>
          Monday morning
        </p>
        <p className="mt-1 text-sm text-walnut" style={{ fontFamily: fontById(body)?.stack }}>
          Calendar math, then CKLA Unit 4. Picture day at 1:15 — 24 students, 0 absent.
        </p>
        <p className="mt-2 text-xs text-stone">
          Headings: {fontById(heading)?.name ?? 'Fraunces'} · Body:{' '}
          {fontById(body)?.name ?? 'Nunito Sans'}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-secondary" onClick={() => setOpen((v) => !v)}>
          {open ? 'Done choosing' : 'Change fonts'}
        </button>

        {!isDefault && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              updateSettings.mutate({
                bodyFont: DEFAULT_BODY_FONT,
                headingFont: DEFAULT_HEADING_FONT,
              })
            }
          >
            Back to the original
          </button>
        )}
      </div>

      {open && (
        <div className="mt-4 space-y-6">
          <FontPicker
            title="Headings and titles"
            hint="The big text at the top of each page. Somewhere to have fun."
            categories={categories}
            selected={heading}
            onPick={(id) => updateSettings.mutate({ headingFont: id })}
          />

          <FontPicker
            title="Everything else"
            hint="Lesson plans, lists and names. Easy to read beats interesting here."
            categories={categories}
            selected={body}
            onPick={(id) => updateSettings.mutate({ bodyFont: id })}
          />
        </div>
      )}
    </SettingsPanel>
  );
}

function FontPicker({
  title,
  hint,
  categories,
  selected,
  onPick,
}: {
  title: string;
  hint: string;
  categories: FontCategory[];
  selected: string;
  onPick: (id: string) => void;
}) {
  return (
    <div>
      <h3 className="text-sm text-espresso">{title}</h3>
      <p className="mb-2 text-xs text-stone">{hint}</p>

      {categories.map((category) => (
        <div key={category} className="mb-3">
          <p className="mb-1.5 text-xs uppercase tracking-wide text-stone">
            {FONT_CATEGORY_LABELS[category]}
          </p>

          <div className="flex flex-wrap gap-2">
            {FONTS.filter((f) => f.category === category).map((font) => (
              <FontButton
                key={font.id}
                font={font}
                selected={font.id === selected}
                onPick={() => onPick(font.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FontButton({
  font,
  selected,
  onPick,
}: {
  font: FontChoice;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      style={{ fontFamily: font.stack }}
      className={[
        'rounded-lg border px-3 py-2 text-base transition-colors',
        selected
          ? 'border-terracotta bg-terracotta text-cream'
          : 'border-sand bg-white text-espresso hover:border-clay hover:bg-linen',
      ].join(' ')}
    >
      {font.name}
    </button>
  );
}

/**
 * Numbering the class lives here rather than on the roster.
 *
 * It is a start-of-year action that rewrites every child's number at once —
 * one stray click in February would undo months of muscle memory, so it sits
 * behind Settings with the count spelled out before you commit.
 */
function ClassNumbering() {
  const { data: students = [] } = useStudents();
  const confirm = useConfirm();
  const renumber = useRenumberStudents();

  const inClass = students.filter((s) => s.active);
  const drift = misnumberedStudents(students);

  return (
    <SettingsPanel title="Class numbers">
      <p className="mb-3 text-sm leading-relaxed text-walnut">
        Numbers students in class 1 through {inClass.length || '…'} in alphabetical order by last
        name. Meant for the start of the year — after that, set a number by hand on the student
        instead, so nobody's number moves without you deciding it should.
      </p>

      {inClass.length === 0 ? (
        <p className="text-xs text-stone">Add students to the roster first.</p>
      ) : (
        <>
          <button
            onClick={async () => {
              const ok = await confirm({
                title: `Renumber all ${inClass.length} students?`,
                body: 'Everyone is numbered alphabetically by last name, and every number you have set by hand is replaced.',
                confirmLabel: 'Renumber the class',
              });
              if (ok) renumber.mutate();
            }}
            className="btn-secondary"
            disabled={renumber.isPending}
          >
            {renumber.isPending ? 'Numbering…' : 'Number the class alphabetically'}
          </button>
          <p className="mt-2 text-xs text-stone">
            {drift.length === 0
              ? 'Numbers currently match alphabetical order.'
              : `${drift.length} number${drift.length === 1 ? '' : 's'} no longer match alphabetical order — expected if you have assigned any by hand.`}
          </p>
        </>
      )}
    </SettingsPanel>
  );
}

/** Your name on the dashboard, and the password that guards the whole thing. */
function AccountSection() {
  const { user } = useAuth();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();

  const [name, setName] = useState(user?.displayName ?? '');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmNext, setConfirmNext] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function saveName() {
    setError(null);
    setMessage(null);
    try {
      await updateProfile.mutateAsync({ displayName: name.trim() });
      setMessage('Name saved.');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That could not be saved.');
    }
  }

  async function savePassword() {
    setError(null);
    setMessage(null);

    if (next !== confirmNext) {
      setError('The two new passwords do not match.');
      return;
    }

    try {
      await changePassword.mutateAsync({ current, next });
      setCurrent('');
      setNext('');
      setConfirmNext('');
      setMessage('Password changed.');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That could not be changed.');
    }
  }

  return (
    <SettingsPanel title="Your account">
      <p className="mb-3 text-sm text-stone">{user?.email}</p>

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1">
          <label className="label" htmlFor="display-name">
            Name
          </label>
          <input
            id="display-name"
            className="field"
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <button
          onClick={() => void saveName()}
          disabled={!name.trim() || name.trim() === user?.displayName || updateProfile.isPending}
          className="btn-secondary"
        >
          Save name
        </button>
      </div>

      <div className="border-t border-sand pt-4">
        <h3 className="mb-1 text-sm">Change your password</h3>
        <p className="mb-3 text-xs leading-snug text-stone">
          Your current password is required — that is what stops someone changing it on an
          unattended laptop and locking you out of your own records. Twelve characters or more.
        </p>

        <div className="grid gap-2 sm:grid-cols-3">
          <input
            className="field"
            type="password"
            placeholder="Current password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            aria-label="Current password"
          />
          <input
            className="field"
            type="password"
            placeholder="New password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            aria-label="New password"
          />
          <input
            className="field"
            type="password"
            placeholder="New password again"
            autoComplete="new-password"
            value={confirmNext}
            onChange={(e) => setConfirmNext(e.target.value)}
            aria-label="New password again"
          />
        </div>

        <button
          onClick={() => void savePassword()}
          disabled={!current || next.length < 12 || changePassword.isPending}
          className="btn-secondary mt-2"
        >
          {changePassword.isPending ? 'Changing…' : 'Change password'}
        </button>
      </div>

      {message && <p className="mt-3 text-sm text-olive">{message}</p>}
      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
          {error}
        </p>
      )}
    </SettingsPanel>
  );
}

/** The school year, which is what "this year" means everywhere else. */
function SchoolYearSection() {
  const { user } = useAuth();
  const updateSettings = useUpdateSettings();
  const [saved, setSaved] = useState(false);

  const start = user?.settings.schoolYearStart?.slice(0, 10) ?? '';
  const end = user?.settings.schoolYearEnd?.slice(0, 10) ?? '';

  async function set(patch: { schoolYearStart?: string | null; schoolYearEnd?: string | null }) {
    setSaved(false);
    await updateSettings.mutateAsync(patch);
    setSaved(true);
  }

  return (
    <SettingsPanel title="School year">
      <p className="mb-3 text-sm text-stone">
        Elkhart's calendar runs into the following summer, so both ends are worth setting.
      </p>

      <div className="flex flex-wrap gap-3">
        <div>
          <label className="label" htmlFor="year-start">
            First day
          </label>
          <input
            id="year-start"
            className="field"
            type="date"
            value={start}
            onChange={(e) => void set({ schoolYearStart: e.target.value || null })}
          />
        </div>
        <div>
          <label className="label" htmlFor="year-end">
            Last day
          </label>
          <input
            id="year-end"
            className="field"
            type="date"
            value={end}
            onChange={(e) => void set({ schoolYearEnd: e.target.value || null })}
          />
        </div>
      </div>

      {saved && <p className="mt-2 text-xs text-olive">Saved.</p>}

      <RollOverWarning start={start} end={end} />

      <PastYears />
    </SettingsPanel>
  );
}

/**
 * What the last day actually does.
 *
 * This is the only setting in the app that changes data rather than
 * appearance, and it does it on a date months away — so it says plainly what
 * will happen, on what day, and to what, in the same place the date is typed.
 */
function RollOverWarning({ start, end }: { start: string; end: string }) {
  const archive = useArchiveSchoolYear();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);

  if (!end) {
    return (
      <p className="mt-4 rounded-lg border border-dashed border-sand p-3 text-xs leading-relaxed text-stone">
        With no last day set, nothing is ever archived and the roster carries on until you clear it
        yourself.
      </p>
    );
  }

  const label = schoolYearLabel(start || null, end);
  const readable = new Date(`${end}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="mt-4 rounded-lg border border-ochre/60 bg-ochre/10 p-3">
      <h3 className="text-sm text-espresso">What happens on the last day</h3>

      <p className="mt-1 text-xs leading-relaxed text-walnut">
        The day after <span className="font-semibold">{readable}</span>, the {label} year is put
        away on its own and next year starts empty. The dates above move on by a year so you do not
        have to set them again.
      </p>

      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-stone">Archived</p>
          <ul className="mt-1 space-y-0.5">
            {ARCHIVED_BY_ROLLOVER.map((line) => (
              <li key={line} className="text-xs leading-snug text-walnut">
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wide text-stone">Kept, untouched</p>
          <ul className="mt-1 space-y-0.5">
            {KEPT_BY_ROLLOVER.map((line) => (
              <li key={line} className="text-xs leading-snug text-walnut">
                {line}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="mt-2 text-xs leading-relaxed text-walnut">
        Nothing is deleted. Every child, note, flag and score stays exactly where it is, tagged with
        the year, and any archived year can be brought back below.
      </p>

      <button
        className="btn-secondary mt-3"
        disabled={archive.isPending}
        onClick={async () => {
          setError(null);
          const ok = await confirm({
            title: `End the ${label} year now?`,
            body:
              'The roster, the seating and the assessment data are archived, and the dates above ' +
              'move on a year. Nothing is deleted, and you can put it back.',
            confirmLabel: 'End the year',
            danger: true,
          });
          if (!ok) return;

          try {
            await archive.mutateAsync();
          } catch (err) {
            setError(err instanceof ApiRequestError ? err.message : 'That did not work');
          }
        }}
      >
        {archive.isPending ? 'Archiving…' : 'End the year now'}
      </button>

      <p className="mt-1 text-[11px] text-stone">
        For when the class leaves before the calendar says so.
      </p>

      {error && <p className="mt-1 text-xs text-rust">{error}</p>}
    </div>
  );
}

/** Years already put away, and the way back. */
function PastYears() {
  const { data: years = [] } = useSchoolYears();
  const restore = useRestoreSchoolYear();
  const confirm = useConfirm();

  return (
    <div className="mt-4">
      <h3 className="mb-1 text-sm text-espresso">Archived years</h3>

      {years.length === 0 && (
        <p className="mb-2 text-sm leading-relaxed text-walnut">
          Nothing is archived yet. Past years will be listed here once a school year ends.
        </p>
      )}

      <ul className="space-y-1">
        {years.map((year) => (
          <li
            key={year.id}
            className="flex flex-wrap items-center gap-2 rounded-lg border border-sand bg-linen/40 px-3 py-2"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm text-espresso">{year.label}</span>
              <span className="block text-xs text-stone">
                {year.students} student{year.students === 1 ? '' : 's'} · {year.assessments}{' '}
                assessment{year.assessments === 1 ? '' : 's'} · archived{' '}
                {new Date(year.archivedAt).toLocaleDateString()}
                {year.manual ? ' by hand' : ''}
              </span>
            </span>

            <button
              className="btn-soft"
              onClick={async () => {
                const ok = await confirm({
                  title: `Bring ${year.label} back?`,
                  body:
                    'Its students and assessments return to the current roster and data, and the ' +
                    'school year dates go back to that year. The seating chart is not refilled — ' +
                    'who sat where was not recorded when they were lifted out.',
                  confirmLabel: 'Bring it back',
                });
                if (ok) restore.mutate(year.id);
              }}
            >
              Bring back
            </button>
          </li>
        ))}
      </ul>

      {/*
        The only way in. Kept out of the sidebar on purpose — this is somewhere
        she goes twice a year, and the sidebar is for the forty-times-a-day work.
      */}
      <Link to="/archives" className="btn-secondary mt-3 inline-flex">
        Browse the archives
      </Link>

      <p className="mt-2 text-[11px] leading-relaxed text-stone">
        The archive is for looking, not editing: past classes, their notes and every score, exactly
        as they were left. Bringing a year back is the only way to change it.
      </p>
    </div>
  );
}

/**
 * A copy of everything, in the teacher's own hands.
 *
 * Not a substitute for the database, but the difference between an outage
 * being an inconvenience and being a lost year of records.
 */
function BackupSection() {
  const [busy, setBusy] = useState<'records' | 'archive' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(kind: 'records' | 'archive') {
    setBusy(kind);
    setError(null);
    try {
      await downloadBackup(kind);
    } catch {
      setError('The download could not be created. Try again in a moment.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingsPanel title="Keep your own copy">
      <p className="mb-3 text-sm leading-relaxed text-walnut">
        Everything on this dashboard lives on one server. These are how you hold a copy of it
        yourself — worth doing at the end of each term, and worth keeping somewhere that is not
        the same computer.
      </p>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => void run('archive')} disabled={!!busy} className="btn-primary">
          {busy === 'archive' ? 'Preparing…' : 'Download everything'}
        </button>

        <button onClick={() => void run('records')} disabled={!!busy} className="btn-secondary">
          {busy === 'records' ? 'Preparing…' : 'Just the records'}
        </button>
      </div>

      <p className="mt-3 text-xs leading-snug text-stone">
        <strong>Everything</strong> is a zip: the records, plus every photograph in the photo book,
        every roster photo and every image in a note, as ordinary picture files. It is large — a
        year of photographs is what it says it is. <strong>Just the records</strong> is one small
        file with no pictures in it, quick enough to take weekly.
      </p>

      <p className="mt-2 text-xs leading-snug text-stone">
        Both hold children's names, written records and, in the first, their faces. Keep them
        somewhere you would be comfortable keeping the paper equivalent.
      </p>

      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
          {error}
        </p>
      )}

      <WeeklyBackupToggle />
      <RestoreControl />
    </SettingsPanel>
  );
}

/**
 * The copy that leaves this server.
 *
 * Everything above is a copy she has to remember to take. This is the one
 * that happens whether or not anybody remembers, and it is the only copy that
 * exists anywhere other than the single volume the database sits on — which
 * is the difference between an outage and a lost year.
 */
function WeeklyBackupToggle() {
  const { user } = useAuth();
  const update = useUpdateSettings();

  const on = user?.settings?.emailBackups ?? false;
  const hasMail = !!user?.email;

  return (
    <div className="mt-4 border-t border-sand pt-3">
      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          checked={on}
          disabled={!hasMail}
          onChange={(e) => update.mutate({ emailBackups: e.target.checked })}
          className="mt-0.5 h-4 w-4 shrink-0 accent-terracotta"
        />
        <span>
          <span className="block text-sm text-espresso">Email me a copy every week</span>
          <span className="block text-xs leading-snug text-stone">
            The records file, sent to {user?.email ?? 'your email'} once a week, using the mail
            account set up in Inbox. Needs no other service, and leaves a copy somewhere this
            dashboard cannot take with it if it goes wrong. Photographs are too large to email —
            take those with “Download everything”.
          </span>
        </span>
      </label>
    </div>
  );
}

/**
 * Putting a backup back.
 *
 * Behind a typed confirmation, and deliberately plain — this is the screen
 * somebody reaches on a bad day, and it should read like an instruction
 * rather than a feature. It says what it will do before it does it, and what
 * it cannot do at all.
 */
function RestoreControl() {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RestoreResult | null>(null);

  const CONFIRMATION = 'REPLACE EVERYTHING';

  async function run() {
    if (!file) return;

    setBusy(true);
    setError(null);
    try {
      const result = await restoreBackup(file, typed);
      setDone(result);
      /*
       * Everything on every page has just been replaced underneath the app.
       * A reload is the honest way to show that, rather than leaving half the
       * screen showing a class that no longer exists.
       */
      setTimeout(() => window.location.reload(), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="mt-4 border-t border-sand pt-3">
        <button className="btn-soft" onClick={() => setOpen(true)}>
          Restore from a backup file
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-card border border-rust/40 bg-rose/5 p-3">
      <h3 className="mb-1 text-base text-espresso">Restore from a backup</h3>

      <p className="mb-2 text-sm leading-relaxed text-walnut">
        This replaces what is on the dashboard now with what is in the file. It cannot be undone,
        so take a backup first if there is anything here worth keeping.
      </p>

      <p className="mb-3 text-xs leading-snug text-stone">
        Photographs do not come back this way — they are not in the file. They are in the zip from
        “Download everything”, as ordinary picture files, and go back through the Photo book page.
      </p>

      <input
        type="file"
        accept="application/json,.json"
        className="field mb-2 text-sm"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />

      <label className="mb-2 block">
        <span className="label">
          Type <strong>{CONFIRMATION}</strong> to confirm
        </span>
        <input className="field" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </label>

      <div className="flex flex-wrap gap-2">
        <button
          className="btn-primary"
          disabled={!file || typed.trim().toUpperCase() !== CONFIRMATION || busy || !!done}
          onClick={() => void run()}
        >
          {busy ? 'Restoring…' : 'Restore'}
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setOpen(false);
            setTyped('');
            setFile(null);
            setError(null);
          }}
        >
          Cancel
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
          {error}
        </p>
      )}

      {done && (
        <div className="mt-3 rounded-lg bg-sage/20 px-3 py-2 text-sm text-olive">
          <p>
            Restored{' '}
            {Object.entries(done.restored)
              .filter(([, n]) => n > 0)
              .map(([key, n]) => `${n} ${key}`)
              .join(', ') || 'nothing — the file was empty'}
            .
          </p>
          {done.skipped.length > 0 && (
            <p className="mt-1 text-xs">
              Left alone, because their pictures are not in this file: {done.skipped.join(', ')}.
            </p>
          )}
          <p className="mt-1 text-xs">Reloading…</p>
        </div>
      )}
    </div>
  );
}
